import { normalizeSampleSummaries } from "@tsmono/inspect-common/normalize";
import {
  AppConfig,
  EvalLog,
  EvalSet,
  LogFilesResponse,
  LogInfo,
  LogUpdate,
  Result,
  SearchInputListResponse,
  SearchRequest,
  SearchResponse,
} from "@tsmono/inspect-common/types";
import { modelRoleNames } from "@tsmono/inspect-common/utils";
import { ApiError, asyncJsonParse, encodeBase64Url } from "@tsmono/util";

import { headlineMetric } from "../../../scoring/headline";
import { fetchPendingSampleDataDirect } from "../../remote/remotePendingSampleData";
import { normalizeEvalLog } from "../../utils/normalize";
import { download_file } from "../shared/api-shared";
import { logNamesDigest } from "../shared/logNamesDigest";
import {
  Capabilities,
  EditLogResult,
  EvalHeader,
  LogContents,
  LogPreview,
  LogRoot,
  LogViewAPI,
  PendingSampleResponse,
  PendingSamples,
  PendingSampleUrls,
  SampleData,
  SampleDataResponse,
  SampleRef,
  SaveLogSubsetResult,
  SearchResultScope,
  UserInfo,
} from "../types";

import {
  HeaderProvider,
  Request,
  serverRequestApi,
  unwrapFastapiDetail,
  VIEW_REQUEST_HEADER,
  VIEW_REQUEST_HEADER_VALUE,
} from "./request";

// The time that the view was initially loaded
const LOADED_TIME = Date.now();

// The time we fetched logs (used for finding client events since last fetch).
// Known exemption from the LogViewAPI no-ambient-state contract: shared
// across instances (and written by the bootstrap probe), but only read by
// `client_events`, which is disabled on the one multi-instance transport
// (VS Code). Move into the instance/transport if that changes.
let lastEvalTime = 0;

/** Transport-only options — everything about *how* to reach the server. */
export interface ViewServerTransport {
  apiBaseUrl?: string;
  headerProvider?: HeaderProvider;
  customFetch?: typeof fetch;
}

const transportRequestApi = (transport: ViewServerTransport) =>
  serverRequestApi(
    transport.apiBaseUrl || __VIEW_SERVER_API_URL__,
    transport.headerProvider,
    transport.customFetch
  );

/**
 * Bootstrap probe: the server's log root (`GET /logs`), optionally for an
 * explicit dir. Dir discovery runs before any `LogViewAPI` exists — the api
 * itself never answers "which dir?" (see #392) — so this is a standalone
 * function over the transport rather than a method on the api.
 */
/**
 * A view-server JSON response, typed by the caller. This is the #555 API
 * boundary: the server is a separate process on its own release cycle, so the
 * shape here is a claim about the contract rather than something checked. Kept
 * in one place so no other line in this module has to assert.
 */
const asResponse = <T>(parsed: unknown): T =>
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- API boundary (#555): see above
  parsed as T;

const isCrossOriginApi = (apiBaseUrl: string | undefined): boolean => {
  try {
    return Boolean(
      apiBaseUrl && new URL(apiBaseUrl).origin !== window.location.origin
    );
  } catch {
    return false;
  }
};

export async function fetchViewServerLogRoot(
  transport: ViewServerTransport = {},
  logDir?: string
): Promise<LogRoot | undefined> {
  const requestApi = transportRequestApi(transport);
  const path = logDir ? `/logs?log_dir=${encodeURIComponent(logDir)}` : "/logs";
  const logs = await requestApi.fetchString("GET", path);

  // Note the last request time so we can get events
  // since the last request
  lastEvalTime = Date.now();
  return asResponse<LogRoot | undefined>(logs.parsed);
}

/**
 * Bootstrap probe: the server's configured log dir (`GET /log-dir`). Used
 * only to resolve a bare single-file ref (`?log_file=foo.eval`) to a dir.
 */
export async function fetchViewServerLogDir(
  transport: ViewServerTransport = {}
): Promise<string | undefined> {
  const requestApi = transportRequestApi(transport);
  const obj = asResponse<{ log_dir?: string }>(
    (await requestApi.fetchString("GET", "/log-dir")).parsed
  );
  return obj.log_dir;
}

/**
 * Create a view server API bound to `logDir`. Every dir-scoped request
 * carries `?log_dir=` so the instance's answers never depend on the
 * server's configured dir (the `LogViewAPI` contract).
 */
export function viewServerApi(
  options: ViewServerTransport & { logDir: string }
): LogViewAPI {
  const { apiBaseUrl, logDir, headerProvider, customFetch } = options;

  const requestApi = serverRequestApi(
    apiBaseUrl || __VIEW_SERVER_API_URL__,
    headerProvider,
    customFetch
  );

  const client_events = async () => {
    const params = new URLSearchParams();
    params.append("loaded_time", String(LOADED_TIME.valueOf()));
    params.append("last_eval_time", String(lastEvalTime.valueOf()));
    const result = await requestApi.fetchString(
      "GET",
      `/events?${params.toString()}`
    );
    return asResponse<string[]>(result.parsed);
  };

  const get_logs = async (
    mtime: number,
    clientFileCount: number,
    clientNames?: readonly string[]
  ) => {
    const path = `/log-files?log_dir=${encodeURIComponent(logDir)}`;

    const headers: Record<string, string> = {};
    const token = log_file_token(mtime, clientFileCount, clientNames);
    if (token) {
      headers["If-None-Match"] = token;
    }

    // Note the last request time so we can get events
    // since the last request
    lastEvalTime = Date.now();

    const envelope = await requestApi.fetchString("GET", path, headers);
    return asResponse<LogFilesResponse>(envelope.parsed);
  };

  const log_file_token = (
    mtime: number,
    fileCount: number,
    names?: readonly string[]
  ) => {
    // Use a weak etag as the mtime and file count may not
    // uniquely identify the state of the log directory. The names digest
    // catches moves and renames, which keep both.
    const digest = names ? `-${logNamesDigest(names)}` : "";
    return `W/"${mtime}-${fileCount}${digest}"`;
  };

  const get_eval_set = async (dir?: string) => {
    const basePath = "/eval-set";
    const params = new URLSearchParams();
    params.append("log_dir", logDir);
    if (dir) {
      params.append("dir", dir);
    }
    const path = `${basePath}?${params.toString()}`;

    try {
      const result = await requestApi.fetchString("GET", path);
      return asResponse<EvalSet>(result.parsed);
    } catch (error) {
      // if the eval set is not found, no biggee as not all
      // log directories will have an eval set.
      if (
        error instanceof ApiError &&
        (error.status === 404 || error.status === 403)
      ) {
        return undefined;
      }
      throw error;
    }
  };

  const get_flow = async (dir?: string) => {
    const basePath = "/flow";
    const params = new URLSearchParams();
    params.append("log_dir", logDir);
    if (dir) {
      params.append("dir", dir);
    }
    const path = `${basePath}?${params.toString()}`;

    try {
      const bytes = await requestApi.fetchBytes("GET", path);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      // if the eval set is not found, no biggee as not all
      // log directories will have an eval set.
      if (
        error instanceof ApiError &&
        (error.status === 404 || error.status === 403)
      ) {
        return undefined;
      }
      throw error;
    }
  };

  const get_log_contents = async (
    file: string,
    headerOnly?: number,
    _capabilities?: Capabilities
  ): Promise<LogContents> => {
    const result = await requestApi.fetchString(
      "GET",
      `/logs/${encodeURIComponent(file)}?header-only=${headerOnly}`
    );
    // Boundary normalization (#555): an older inspect_ai server (version
    // skew is routine in the VS Code extension) serves shapes the current
    // generated types don't admit.
    return { raw: result.raw, parsed: normalizeEvalLog(result.parsed) };
  };

  const get_log_info = async (file: string): Promise<LogInfo> => {
    const result = await requestApi.fetchString(
      "GET",
      `/log-info/${encodeURIComponent(file)}`
    );
    return asResponse<LogInfo>(result.parsed);
  };

  const toLogPreview = (header: EvalHeader): LogPreview => {
    const primary_metric = headlineMetric(
      header.results,
      header.eval.headline_metric
    );

    const model_roles = modelRoleNames(header.eval.model_roles);

    return {
      eval_id: header.eval.eval_id,
      run_id: header.eval.run_id,

      task: header.eval.task,
      task_id: header.eval.task_id,
      task_version: header.eval.task_version,

      version: header.version,
      status: header.status,
      error: header.error,

      model: header.eval.model,
      model_roles,

      started_at: header.stats?.started_at,
      completed_at: header.stats?.completed_at,

      primary_metric,
    };
  };

  const get_log_bytes = async (
    file: string,
    start: number,
    end: number
  ): Promise<Uint8Array> =>
    requestApi.fetchBytes(
      "GET",
      `/log-bytes/${encodeURIComponent(file)}?start=${start}&end=${end}`
    );

  const get_log_summaries = async (files: string[]) => {
    const params = new URLSearchParams();
    for (const file of files) {
      params.append("file", file);
    }
    const result = await requestApi.fetchString(
      "GET",
      `/log-headers?${params.toString()}`
    );
    // Boundary normalization (#555): see get_log_contents. normalizeEvalLog
    // (not normalizeEvalHeader) so v1-shaped results reshape here too.
    const logHeaders = Array.isArray(result.parsed)
      ? result.parsed.map(normalizeEvalLog)
      : [];
    return logHeaders.map(toLogPreview);
  };

  const log_message = async (
    log_file: string,
    message: string
  ): Promise<void> => {
    const params = new URLSearchParams();
    params.append("log_file", log_file);
    params.append("message", message);

    const request: Request<void> = {
      headers: {
        "Content-Type": "text/plain",
      },
      parse: (text: string) => {
        if (text !== "") {
          throw new Error(`Unexpected response from log_message: ${text}`);
        }
        return Promise.resolve();
      },
    };
    await requestApi.fetchType<void>(
      "POST",
      `/log-message?${params.toString()}`,
      request
    );
  };

  const eval_pending_samples = async (
    log_file: string,
    etag?: string
  ): Promise<PendingSampleResponse> => {
    const params = new URLSearchParams();
    params.append("log", log_file);

    const headers: Record<string, string> = {};
    if (etag) {
      headers["If-None-Match"] = etag;
    }

    const request: Request<PendingSampleResponse> = {
      headers,
      parse: async (text: string) => {
        const parsed = await asyncJsonParse<PendingSamples | undefined>(text);
        // Boundary normalization (#555): the pending buffer is served by
        // whatever inspect_ai version is running the eval — fill the
        // read-time defaults old servers omit.
        const pendingSamples = parsed
          ? { ...parsed, samples: normalizeSampleSummaries(parsed.samples) }
          : parsed;
        return {
          status: "OK",
          pendingSamples,
        };
      },
      handleError: (status: number) => {
        if (status === 404) {
          return {
            status: "NotFound",
          };
        } else if (status === 304) {
          return {
            status: "NotModified",
          };
        }
      },
    };

    const result = await requestApi.fetchType<PendingSampleResponse>(
      "GET",
      `/pending-samples?${params.toString()}`,
      request
    );

    return result.parsed;
  };

  const eval_log_sample_data = async (
    log_file: string,
    id: string | number,
    epoch: number,
    last_event?: number,
    last_attachment?: number,
    last_message_pool?: number,
    last_call_pool?: number
  ): Promise<SampleDataResponse | undefined> => {
    const params = new URLSearchParams();
    params.append("log", log_file);
    params.append("id", String(id));
    params.append("epoch", String(epoch));
    if (last_event !== undefined) {
      params.append("last-event-id", String(last_event));
    }

    if (last_attachment !== undefined) {
      params.append("after-attachment-id", String(last_attachment));
    }

    if (last_message_pool !== undefined) {
      params.append("after-message-pool-id", String(last_message_pool));
    }

    if (last_call_pool !== undefined) {
      params.append("after-call-pool-id", String(last_call_pool));
    }

    const request: Request<SampleDataResponse> = {
      headers: {},
      parse: async (text: string) => {
        const sampleData = await asyncJsonParse<SampleData | undefined>(text);
        return {
          status: "OK",
          sampleData,
        };
      },
      handleError: (status: number) => {
        if (status === 404) {
          return {
            status: "NotFound",
          };
        } else if (status === 304) {
          return {
            status: "NotModified",
          };
        }
      },
    };

    const result = await requestApi.fetchType<SampleDataResponse>(
      "GET",
      `/pending-sample-data?${params.toString()}`,
      request
    );

    return result.parsed;
  };

  const get_pending_sample_data_urls = async (
    log_file: string,
    id: string | number,
    epoch: number,
    last_event?: number,
    last_attachment?: number,
    last_message_pool?: number,
    last_call_pool?: number,
    max_segments?: number
  ): Promise<PendingSampleUrls> => {
    const params = new URLSearchParams();
    params.append("log", log_file);
    params.append("id", String(id));
    params.append("epoch", String(epoch));
    if (last_event !== undefined) {
      params.append("last-event-id", String(last_event));
    }

    if (last_attachment !== undefined) {
      params.append("after-attachment-id", String(last_attachment));
    }

    if (last_message_pool !== undefined) {
      params.append("after-message-pool-id", String(last_message_pool));
    }

    if (last_call_pool !== undefined) {
      params.append("after-call-pool-id", String(last_call_pool));
    }

    if (max_segments !== undefined) {
      params.append("max-segments", String(max_segments));
    }

    const request: Request<PendingSampleUrls> = {
      headers: {},
      parse: async (text: string) => {
        return await asyncJsonParse<PendingSampleUrls>(text);
      },
    };

    const result = await requestApi.fetchType<PendingSampleUrls>(
      "GET",
      `/pending-sample-data-urls?${params.toString()}`,
      request
    );
    return result.parsed;
  };

  const eval_log_sample_data_direct = async (
    log_file: string,
    id: string | number,
    epoch: number,
    last_event?: number,
    last_attachment?: number,
    last_message_pool?: number,
    last_call_pool?: number
  ): Promise<SampleDataResponse | undefined> => {
    const direct = await fetchPendingSampleDataDirect(
      get_pending_sample_data_urls,
      log_file,
      id,
      epoch,
      { last_event, last_attachment, last_message_pool, last_call_pool }
    );
    if (direct === undefined) {
      return undefined;
    }
    return {
      status: "OK",
      sampleData: direct.sampleData,
      has_more: direct.has_more,
      complete: direct.complete,
    };
  };

  const edit_log = async (
    log_file: string,
    update: LogUpdate,
    if_match_etag?: string
  ): Promise<EditLogResult> => {
    // fetch directly (rather than via serverRequestApi) so we can read the
    // ETag response header alongside the JSON body — fetchType only surfaces
    // the parsed body.
    const baseUrl = apiBaseUrl || __VIEW_SERVER_API_URL__;
    const path = `/log-edit/${encodeURIComponent(log_file)}`;
    const url = baseUrl ? `${baseUrl.replace(/\/$/, "")}${path}` : path;

    const headers: Record<string, string> = {
      Accept: "application/json",
      "Content-Type": "application/json",
    };
    if (if_match_etag) {
      headers["If-Match"] = if_match_etag;
    }
    if (headerProvider) {
      Object.assign(headers, await headerProvider());
    }
    headers[VIEW_REQUEST_HEADER] = VIEW_REQUEST_HEADER_VALUE;

    const isCrossOrigin = Boolean(
      apiBaseUrl &&
      (() => {
        try {
          return new URL(apiBaseUrl).origin !== window.location.origin;
        } catch {
          return false;
        }
      })()
    );

    const response = await (customFetch ?? fetch)(url, {
      method: "POST",
      headers,
      body: JSON.stringify(update),
      credentials: isCrossOrigin ? "include" : "same-origin",
    });

    if (!response.ok) {
      // FastAPI wire-encodes `HTTPException(detail=...)` as
      // `{"detail": "..."}`. Unwrap so the dialog's `formatEditError`
      // (and any other ApiError consumer) sees the human-readable
      // message instead of the JSON envelope or an "API Error N:"
      // prefix — status-specific server responses (400 validation,
      // 409 in-progress, 412 stale ETag) need to render as themselves.
      const rawText = await response.text();
      const message = unwrapFastapiDetail(rawText) || response.statusText;
      throw new ApiError(response.status, message);
    }

    const text = await response.text();
    const log = await asyncJsonParse<EvalLog>(text);
    const etag = response.headers.get("ETag") ?? undefined;
    return { log, etag };
  };

  const get_user_info = async (): Promise<UserInfo> => {
    const result = await requestApi.fetchString("GET", "/user-info");
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return asResponse<UserInfo>(result.parsed) ?? {};
  };

  const get_app_config = async (): Promise<AppConfig> => {
    const result = await requestApi.fetchString("GET", "/app-config");
    return asResponse<AppConfig>(result.parsed);
  };

  const list_searches = async (
    search_type: "grep" | "llm",
    count: number
  ): Promise<SearchInputListResponse> => {
    const params = new URLSearchParams();
    params.append("type", search_type);
    params.append("count", String(count));
    const result = await requestApi.fetchString(
      "GET",
      `/scout/searches?${params.toString()}`
    );
    return asResponse<SearchInputListResponse>(result.parsed);
  };

  const post_search = async (
    transcriptDir: string,
    transcriptId: string,
    request: SearchRequest
  ): Promise<SearchResponse> => {
    const result = await requestApi.fetchString(
      "POST",
      `/scout/transcripts/${encodeBase64Url(transcriptDir)}/${encodeURIComponent(transcriptId)}/search`,
      { "Content-Type": "application/json" },
      JSON.stringify(request)
    );
    return asResponse<SearchResponse>(result.parsed);
  };

  const get_search_result = async (
    transcriptDir: string,
    transcriptId: string,
    search_id: string,
    scope: SearchResultScope
  ): Promise<Result | null> => {
    const params = new URLSearchParams();
    if (scope.messages) params.set("messages", scope.messages);
    if (scope.events) params.set("events", scope.events);
    const query = params.toString();
    const path =
      `/scout/transcripts/${encodeBase64Url(transcriptDir)}/${encodeURIComponent(transcriptId)}` +
      `/searches/${encodeURIComponent(search_id)}${query ? `?${query}` : ""}`;
    try {
      const result = await requestApi.fetchString("GET", path);
      return asResponse<Result>(result.parsed);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return null;
      }
      throw error;
    }
  };

  // Direct fetch (as in `edit_log`) so a FastAPI `{"detail": ...}` error
  // surfaces as its message and a binary body stays readable as a Blob.
  const post_json = async (path: string, body: unknown): Promise<Response> => {
    const baseUrl = apiBaseUrl || __VIEW_SERVER_API_URL__;
    const url = baseUrl ? `${baseUrl.replace(/\/$/, "")}${path}` : path;
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (headerProvider) {
      Object.assign(headers, await headerProvider());
    }
    headers[VIEW_REQUEST_HEADER] = VIEW_REQUEST_HEADER_VALUE;

    const response = await (customFetch ?? fetch)(url, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      credentials: isCrossOriginApi(apiBaseUrl) ? "include" : "same-origin",
    });
    if (!response.ok) {
      const message =
        unwrapFastapiDetail(await response.text()) || response.statusText;
      throw new ApiError(response.status, message);
    }
    return response;
  };

  const download_log_subset = async (
    log_file: string,
    samples: SampleRef[],
    file_name: string
  ): Promise<void> => {
    const response = await post_json(
      `/log-subset-download/${encodeURIComponent(log_file)}`,
      { samples }
    );
    await download_file(file_name, await response.blob());
  };

  const save_log_subset = async (
    log_file: string,
    samples: SampleRef[],
    file_name: string
  ): Promise<SaveLogSubsetResult> => {
    const response = await post_json(
      `/log-subset-save/${encodeURIComponent(log_file)}`,
      { samples, file_name }
    );
    return asResponse<SaveLogSubsetResult>(
      await asyncJsonParse(await response.text())
    );
  };

  const savedViewsPath = (folder: string) =>
    `/saved-views?folder=${encodeURIComponent(folder)}`;

  const get_saved_views = async (folder: string): Promise<unknown> =>
    (await requestApi.fetchString("GET", savedViewsPath(folder))).parsed;

  const put_saved_views = async (
    folder: string,
    views: readonly object[]
  ): Promise<void> => {
    await requestApi.fetchString(
      "PUT",
      savedViewsPath(folder),
      {},
      JSON.stringify({ views })
    );
  };

  const download_log = (log_file: string): Promise<void> => {
    const baseUrl = apiBaseUrl || __VIEW_SERVER_API_URL__;
    const url = `${baseUrl}/log-download/${encodeURIComponent(log_file)}`;

    const link = document.createElement("a");
    link.href = url;
    link.download = "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return Promise.resolve();
  };

  return {
    client_events,
    get_logs,
    get_eval_set,
    get_flow,
    get_log_contents,
    get_log_info,
    get_log_bytes,
    get_log_summaries,
    log_message,
    download_file,
    download_log,
    open_log_file: async () => {},
    eval_pending_samples,
    eval_log_sample_data,
    eval_log_sample_data_direct,
    edit_log,
    download_log_subset,
    save_log_subset,
    get_saved_views,
    put_saved_views,
    get_user_info,
    get_app_config,
    list_searches,
    post_search,
    get_search_result,
  };
}
